import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import { METHOD_STEPS, PHASES } from "@/data/diet";

export default function DietHowItWorks() {
  return (
    <div className="min-h-screen bg-[#1A211A] text-[#F7F5F0]">
      <Nav />
      <header className="pt-32 md:pt-44 pb-10 px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#A9B39D]">Powered by JAY 7™</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">How JAY works.</h1>
          <p className="mt-5 max-w-xl mx-auto text-[#C9CFC0] text-sm md:text-base leading-relaxed">
            A step-by-step journey — scroll through the method that takes you from assessment to nutrition independence.
          </p>
          <ArrowDown size={20} className="mx-auto mt-8 text-[#C57B57] animate-bounce" />
        </motion.div>
      </header>

      {/* Presentation flow */}
      <section className="max-w-3xl mx-auto px-6 pb-10" data-testid="method-flow">
        {METHOD_STEPS.map((s, i) => (
          <div key={s.id}>
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              data-testid={`method-step-${s.id}`}
              className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-8 md:p-12 text-center"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#A9B39D]">Step {i + 1} of {METHOD_STEPS.length}</div>
              <div className="font-serif-display text-3xl md:text-5xl text-[#E8C9B8] tracking-[0.08em] mt-3">{s.name}</div>
              <p className="mt-4 text-sm md:text-base text-[#C9CFC0] leading-relaxed max-w-lg mx-auto">{s.desc}</p>
            </motion.div>
            {i < METHOD_STEPS.length - 1 && (
              <motion.div initial={{ opacity: 0, scaleY: 0 }} whileInView={{ opacity: 1, scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="flex justify-center py-3">
                <div className="w-px h-12 bg-gradient-to-b from-[#C57B57] to-transparent" />
              </motion.div>
            )}
          </div>
        ))}
      </section>

      {/* Phase timeline */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <div className="text-[11px] uppercase tracking-[0.35em] text-[#A9B39D] text-center">The JAY Phase System</div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-center tracking-tight mt-3 mb-12">Where your journey can take you.</h2>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/15" />
          <div className="space-y-8">
            {PHASES.map((p, i) => (
              <motion.div key={p.n} initial={{ opacity: 0, x: i % 2 ? 40 : -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }}
                className={`relative pl-12 md:pl-0 md:w-1/2 ${i % 2 ? "md:ml-auto md:pl-12" : "md:pr-12 md:text-right"}`}>
                <span className={`absolute top-1.5 left-2.5 md:left-auto ${i % 2 ? "md:-left-1.5" : "md:-right-1.5"} h-3 w-3 rounded-full bg-[#C57B57]`} />
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#A9B39D]">Phase {p.n} · {p.days}</div>
                <div className="font-serif-display text-2xl text-[#E8C9B8]">{p.name}</div>
                <p className="text-sm text-[#C9CFC0] mt-1">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="text-center mt-16">
          <a href="/diet#programmes" data-testid="how-cta" className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
            Start Your JAY Journey <ArrowRight size={15} />
          </a>
        </div>
      </section>
    </div>
  );
}
